import React, { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SmsModal } from "@features/SmsModal/SmsModal";
import { Button } from "@components/Button";
import { HouseIcon } from "@components/icons";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { useUpdateJointBillServiceProviderBillPlanMutation } from "@services/jointBillServiceProviderBillPlans/mutations";
import { type PlanId } from "@services/jointBillServiceProviderBillPlans/types";
import { useJointBillServiceSettingsQuery } from "@services/jointBillServiceSettings/queries";
import { MaxPlanRulesContent } from "./components/MaxPlanRulesContent/MaxPlanRulesContent";
import { MaxPlanRulesFiles } from "./components/MaxPlanRulesContent/MaxPlanRulesFiles";

interface IFormData {
  jointBillServiceId: number;
  jointBillServicePlanId: PlanId;
  contactInformation: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    personalCode: string;
    skipUsernameUpdate: boolean;
  };
}

interface IMaxPlanInformationFormModalProps extends IUseModalProps {
  jointBillServiceId: number;
  jointBillServicePlanId: PlanId;
  onComplete: () => void;
}

export const MaxPlanInformationFormModal: React.FC<
  IMaxPlanInformationFormModalProps
> = (props) => {
  const { jointBillServicePlanId, onComplete, jointBillServiceId } = props;

  const [planRulesAccepted, setPlanRulesAccepted] = useState<boolean>(false);

  const [planFormRulesAccepted, setPlanFormRulesAccepted] =
    useState<boolean>(false);
  const [planFormPrivacyAccepted, setPlanFormPrivacyAccepted] =
    useState<boolean>(false);

  const { create } = useModalStore();
  const modal = useModal(props);
  const { t } = useTranslation();

  const { data: customerData } = useCustomerDetailsQuery();
  const { data: jointBillServiceSettings } =
    useJointBillServiceSettingsQuery(jointBillServiceId);

  const { handleSubmit, control, setError } = useForm<IFormData>({
    defaultValues: {
      jointBillServiceId: jointBillServiceId,
      jointBillServicePlanId: jointBillServicePlanId,
      contactInformation: {
        firstName: customerData?.firstName,
        lastName: customerData?.lastName,
        email: customerData?.email,
        phone: customerData?.phone,
        personalCode: "",
        skipUsernameUpdate: true,
      },
    },
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync, isPending } =
    useUpdateJointBillServiceProviderBillPlanMutation({
      onSuccess: () => {
        toast.success(t("plan.toast.success"));
        modal.close();

        if (!jointBillServiceSettings?.isSmsMessagesSubscribed) {
          create((modalProps) => (
            <SmsModal {...modalProps} jointBillServiceId={jointBillServiceId} />
          ));
        }

        onComplete();
      },
    });

  const onFormSubmit: SubmitHandler<IFormData> = async (data) => {
    await mutateAsync(data).catch(setApiErrors);
  };

  return (
    <Modal
      modal={modal}
      title={
        planRulesAccepted
          ? t("plan.modal.maxPlanForm.title")
          : t(`planMaxDocumentation.${jointBillServicePlanId}.title`)
      }
      subtitle={planRulesAccepted ? t("plan.modal.maxPlanForm.subtitle") : ""}
      badgeProps={{ icon: HouseIcon }}
      actionButtons={
        <div className="flex w-full flex-row justify-center gap-3">
          <Button
            variant="secondary"
            className="w-full max-w-72"
            onClick={modal.close}
          >
            {t("common.form.buttons.cancel")}
          </Button>
          {planRulesAccepted ? (
            <Button
              isLoading={isPending}
              disabled={
                !planFormPrivacyAccepted || !planFormRulesAccepted || isPending
              }
              className="w-full max-w-72"
              onClick={handleSubmit(onFormSubmit)}
            >
              {t("common.form.buttons.submit")}
            </Button>
          ) : (
            <Button
              className="w-full max-w-72"
              onClick={() => setPlanRulesAccepted(true)}
            >
              {t("common.form.buttons.metRules")}
            </Button>
          )}
        </div>
      }
      className="min-w-full lg:w-full xl:min-w-[72rem]"
    >
      {!planRulesAccepted ? (
        <MaxPlanRulesContent jointBillServicePlanId={jointBillServicePlanId} />
      ) : (
        <div className="flex flex-col justify-center gap-6 lg:flex-row">
          <div>
            <form className="flex flex-col gap-4 lg:w-96">
              <FormInput
                name="contactInformation.firstName"
                control={control}
                label={t("common.form.fields.firstName.label")}
              />
              <FormInput
                name="contactInformation.lastName"
                control={control}
                label={t("common.form.fields.lastName.label")}
              />
              <FormInput
                name="contactInformation.email"
                control={control}
                label={t("common.form.fields.email.label")}
              />
              <FormInput
                name="contactInformation.phone"
                control={control}
                label={t("common.form.fields.phone.label")}
              />
              <FormInput
                name="contactInformation.personalCode"
                control={control}
                label={t("common.form.fields.personalCode.label")}
              />
            </form>
          </div>
          <div className="flex flex-col gap-6">
            <MaxPlanRulesFiles
              jointBillServicePlanId={jointBillServicePlanId}
            />
            <div className="flex flex-col gap-2">
              <Checkbox
                label={t("planMaxDocumentation.checkbox.confirmFiles")}
                checked={planFormRulesAccepted}
                onChange={(_ev) => {
                  setPlanFormRulesAccepted(_ev.currentTarget.checked);
                }}
              />
              <Checkbox
                label={t(
                  `planMaxDocumentation.${jointBillServicePlanId}.rulesCheck`,
                )}
                checked={planFormPrivacyAccepted}
                onChange={(_ev) => {
                  setPlanFormPrivacyAccepted(_ev.currentTarget.checked);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
